import { useDisplayPairingRecovery } from '../../hooks/useDisplayPairingRecovery';
import '../../styles/tv-entry.css';
import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { isTvAcceptanceEnabled } from '../../lib/tvAcceptance';
import { useTranslation } from 'react-i18next';
import { useFullscreen } from '../../hooks/useFullscreen';
import { useSiteName } from '../../hooks/useSiteName';
import { writeDisplaySessionCredential } from '../../lib/displaySession';
import { timeoutSignal } from '../../lib/timeoutSignal';

interface PublicVenue {
  code: string;
  name: string;
}

export default function TVLauncher() {
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [venues, setVenues] = useState<PublicVenue[]>([]);
  const { t } = useTranslation('display');
  const navigate = useNavigate();
  const siteName = useSiteName();
  const { requestFullscreen, isSupported } = useFullscreen();
  const apiBase =
    (import.meta as unknown as { env: { VITE_API_URL?: string } }).env.VITE_API_URL ?? '';

  useEffect(() => {
    document.title = siteName;
    const controller = new AbortController();
    void fetch(apiBase + '/api/display/venues/public', { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) return [];
        const body = await response.json();
        return Array.isArray(body) ? body : body.data;
      })
      .then((rows) => setVenues(Array.isArray(rows) ? rows : []))
      .catch(() => {});
    return () => controller.abort();
  }, [apiBase, siteName]);

  const paired = async (credential: string) => {
    if (!writeDisplaySessionCredential(credential)) throw new Error('storage failed');
    if (isSupported) await requestFullscreen().catch(() => {});
    navigate('/tv/paired', { replace: true });
  };
  const recovery = useDisplayPairingRecovery(apiBase, paired);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (recovery.state !== 'idle') return;
    if (!/^\d{6}$/.test(code)) {
      setError(t('pairingCodeFormatError'));
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const response = await fetch(apiBase + '/api/display/pairing/exchange', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
        signal: timeoutSignal(5000),
      });
      if (!response.ok) {
        setError(
          t(
            response.status === 429
              ? 'pairingLimited'
              : response.status === 503
                ? 'pairingUnavailable'
                : 'pairingCodeInvalid',
          ),
        );
        return;
      }
      const body = await response.json();
      const result = body.data ?? body;
      await paired(result.credential ?? '');
    } catch {
      setError(t('pairingCodeInvalid'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="tv-entry min-h-screen bg-slate-950 px-8 py-10 text-slate-100">
      <main className="mx-auto max-w-xl">
        <h1 className="text-3xl font-bold">{siteName}</h1>
        {isTvAcceptanceEnabled && (
          <Link
            to="/tv/acceptance"
            className="mt-5 block rounded-xl border-2 border-amber-400 p-4 text-xl focus:outline focus:outline-4 focus:outline-white"
          >
            TV 實機驗收與畫面校正
          </Link>
        )}
        <p className="mt-3 text-2xl text-amber-300">{t('tvPairing')}</p>
        <p className="mt-2 text-lg text-slate-300">{t('tvPairingHint')}</p>
        <form onSubmit={(event) => void submit(event)} className="mt-10 space-y-6">
          <label className="block text-lg font-semibold" htmlFor="pairing-code">
            {t('pairingCodeLabel')}
          </label>
          <input
            id="pairing-code"
            autoFocus
            autoComplete="one-time-code"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
            className="w-full rounded-2xl border-2 border-slate-600 bg-slate-900 px-6 py-5 text-center font-mono text-5xl tracking-[0.3em] focus:border-amber-400 focus:outline-none"
            aria-describedby={error ? 'pairing-error' : undefined}
          />
          {error && (
            <p id="pairing-error" role="alert" className="text-lg text-red-300">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={submitting || code.length !== 6 || recovery.state !== 'idle'}
            className="w-full rounded-2xl bg-amber-400 px-6 py-5 text-xl font-bold text-slate-950 disabled:opacity-50"
          >
            {submitting ? t('pairingInProgress') : t('startPairing')}
          </button>
        </form>
        <section className="mt-8 space-y-4" aria-label={t('recoveryTitle')}>
          <button
            type="button"
            onClick={() => void recovery.start()}
            disabled={recovery.state === 'pending' || recovery.state === 'paused'}
            className="w-full rounded-xl border-2 border-amber-400 p-4 text-lg disabled:opacity-50"
          >
            {t('recoveryTitle')}
          </button>
          {recovery.identifier && (
            <>
              <p>{t('recoveryHint')}</p>
              <p data-testid="recovery-identifier" className="break-all font-mono text-2xl">
                {recovery.identifier}
              </p>
              <p role="status">{t(`recoveryState_${recovery.state}`)}</p>
              {recovery.state === 'paused' && (
                <button type="button" onClick={recovery.resume}>
                  {t('recoveryRetry')}
                </button>
              )}
              <button type="button" onClick={recovery.abandon}>
                {t('recoveryCancel')}
              </button>
            </>
          )}
          {recovery.state === 'unavailable' && <p role="alert">{t('recoveryState_unavailable')}</p>}
        </section>
        {venues.length > 0 && (
          <section
            className="mt-12 border-t border-slate-700 pt-6"
            aria-label={t('pairableVenues')}
          >
            <p className="text-sm text-slate-400">{t('pairableVenues')}</p>
            <ul className="mt-3 flex flex-wrap gap-2">
              {venues.map((venue) => (
                <li key={venue.code} className="rounded-full bg-slate-800 px-4 py-2 text-slate-300">
                  {venue.name}
                </li>
              ))}
            </ul>
          </section>
        )}
      </main>
    </div>
  );
}
